// Re-created from OptimizationDetailModalV2.jsx (lines ~43-152), where it is
// defined inline and (per project decision) must stay untouched. If you update
// the modal's inline version, update here too. Used by archetype F (generic
// sub-items; placements use PlacementsSubItemsTable instead).
import React from 'react';
import { formatCurrencyAmount } from 'utils/currency';

const formatCurrencyNoDecimals = (value, currencyCode = 'USD') => {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return formatCurrencyAmount(Math.round(n), currencyCode, {
    locale: 'es-ES',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    display: 'code',
  });
};

const SubItemsTable = ({
  subItems,
  subItemsSummary,
  currency = 'USD',
  primaryKpi = 'ROAS',
  selectedIds = null,
  onToggle = null,
}) => {
  if (!Array.isArray(subItems) || subItems.length === 0) return null;
  const summary = subItemsSummary && typeof subItemsSummary === 'object' ? subItemsSummary : null;
  const isMultiCurrency = summary?.currency === 'Múltiples monedas';
  const selectable = Array.isArray(selectedIds) && typeof onToggle === 'function';
  const selectedSet = selectable ? new Set(selectedIds.map((x) => String(x))) : null;
  const selectedCount = selectable ? selectedSet.size : (summary?.selected_count ?? summary?.total_count ?? subItems.length);
  const totalCount = summary?.total_count ?? subItems.length;
  return (
    <div className="mt-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs tracking-wide text-white/60 font-semibold">Items sugeridos</div>
          <div className="mt-0.5 text-[11px] text-white/55">
            {selectable
              ? 'Tildá los ítems que querés aplicar.'
              : 'Revisá los elementos que están afectando performance.'}
          </div>
        </div>
        <span className="shrink-0 inline-flex items-center rounded-full border border-white/10 bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white/80 whitespace-nowrap">
          {selectedCount}/{totalCount} seleccionados
        </span>
      </div>

      <div className="mt-3 grid grid-cols-6 gap-3 text-[10px] uppercase tracking-wider text-white/45">
        <div className="col-span-2 truncate">Item</div>
        <div className="truncate">Tipo</div>
        <div className="text-right">Gasto</div>
        <div className="text-right">{String(primaryKpi || 'ROAS')}</div>
        <div className="text-right">Conv.</div>
      </div>

      <div className="mt-2 space-y-2">
        {subItems.slice(0, 8).map((it, idx) => {
          const name = it?.name || it?.keyword || it?.term || 'Item';
          const type = it?.type || 'ITEM';
          const metrics = it?.metrics || {};
          const spend = metrics?.spend ?? metrics?.cost ?? null;
          const roas = metrics?.roas ?? null;
          const cpa = metrics?.cpa ?? null;
          const conv = metrics?.conversions ?? null;
          const itemId = it?.id != null ? String(it.id) : null;
          const isChecked = selectable && itemId != null
            ? selectedSet.has(itemId)
            : (it?.selected_by_default !== false);
          return (
            <div key={`${itemId || name}-${idx}`} className="rounded-lg border border-white/10 bg-white/5 px-3 py-2">
              <div className="grid grid-cols-6 gap-3 items-center">
                <div className="col-span-2 min-w-0">
                  <div className="flex items-center gap-2 min-w-0">
                    {selectable && itemId != null ? (
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => onToggle(itemId)}
                        aria-label={`Incluir ${name}`}
                        className="h-4 w-4 shrink-0 rounded border-white/30 bg-white/5 accent-emerald-500 cursor-pointer"
                      />
                    ) : (
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${isChecked ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100' : 'border-white/10 bg-white/5 text-white/60'}`}>
                        {isChecked ? 'Incluido' : 'Opcional'}
                      </span>
                    )}
                    <div className="text-sm font-semibold text-white truncate" title={name}>{name}</div>
                  </div>
                </div>
                <div className="text-[11px] text-white/70 truncate" title={String(type)}>{String(type)}</div>
                <div className="text-right text-sm font-semibold text-white tabular-nums whitespace-nowrap">
                  {spend != null ? formatCurrencyNoDecimals(spend, currency) : '—'}
                </div>
                <div className="text-right text-sm font-semibold tabular-nums whitespace-nowrap text-white">
                  {String(primaryKpi || '').toUpperCase() === 'CPA'
                    ? (cpa != null ? formatCurrencyNoDecimals(cpa, currency) : '—')
                    : (roas != null ? `${Number(roas).toFixed(1)}x` : '—')}
                </div>
                <div className="text-right text-sm font-semibold text-white tabular-nums whitespace-nowrap">
                  {conv != null ? conv : '—'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {summary?.total_cost != null || isMultiCurrency ? (
        <div className="mt-3 pt-2 border-t border-white/10">
          {isMultiCurrency ? (
            <span className="inline-flex items-center rounded-full border border-amber-400/35 bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-100 whitespace-nowrap">
              Múltiples monedas — totales no aplicables
            </span>
          ) : (
            <div className="flex justify-between items-center text-xs text-white/70">
              <span>Total gasto</span>
              <span className="font-semibold text-white tabular-nums">
                {formatCurrencyNoDecimals(summary.total_cost, summary.currency || currency)}
              </span>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default SubItemsTable;
